/**
 * Break-glass pieces of the booking progress bar. Each one is here because no
 * ui-core primitive can say it:
 *
 *  - `BookShipmentBookingProgressTrack` — the fill's width is a live percentage,
 *    so it has to be an inline style, and the sheen that keeps the bar looking
 *    alive between percentage jumps is an `::after` gradient animation.
 *  - `BookShipmentBookingProgressStage` / `...Slide` — both size themselves off
 *    the *viewport height* (`@media (max-height: 799px)`), which is not one of
 *    Tailwind's width breakpoints and has no prop equivalent. The icon is the
 *    first thing to go on a short viewport — the title and description carry the
 *    message on their own.
 *  - `BookShipmentBookingProgressFrame` — pulls itself up over the screen above
 *    it with a negative margin and caps at 800px.
 */

import type { ReactNode } from "react";
import { T } from "@freightsimple/ui-core/Typography/T";

interface ChildrenProps {
  children: ReactNode;
}

export function BookShipmentBookingProgressFrame({ children }: ChildrenProps) {
  return (
    <div className="relative -mt-[150px] w-full max-w-[800px] p-10">
      {children}
    </div>
  );
}

export function BookShipmentBookingProgressStage({ children }: ChildrenProps) {
  return (
    <div className="flex h-[300px] w-full items-center justify-center overflow-hidden py-5 [@media(max-height:799px)]:h-[200px]">
      {children}
    </div>
  );
}

interface BookShipmentBookingProgressSlideProps {
  icon: ReactNode;
  title: string;
  description: string;
}

export function BookShipmentBookingProgressSlide({
  icon,
  title,
  description,
}: BookShipmentBookingProgressSlideProps) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-5 p-10 text-center transition-all duration-300 ease-[ease]">
      <div className="h-20 w-20 rounded-full [@media(max-height:799px)]:hidden">
        {icon}
      </div>
      <div className="mb-2 text-center">
        <T size="2xl" weight="semibold">
          {title}
        </T>
      </div>
      <div className="max-w-[400px] text-center leading-normal">
        <T color="muted">{description}</T>
      </div>
    </div>
  );
}

interface BookShipmentBookingProgressTrackProps {
  /** How full the bar is, 0–100. */
  percentage: number;
}

export function BookShipmentBookingProgressTrack({
  percentage,
}: BookShipmentBookingProgressTrackProps) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-sm bg-secondary">
      <div
        className="after:animate-progress-sheen relative h-full rounded-sm bg-linear-to-r from-light-blue to-brand transition-[width] duration-[800ms] ease-in-out after:absolute after:inset-0 after:bg-linear-to-r after:from-transparent after:via-white/30 after:to-transparent after:content-['']"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}
